/**
 * Y-axis auto-range utilities.
 *
 * Several charts in this codebase compute a padded [yMin, yMax] range from a
 * stream of candidate values, optionally skipping certain dates (e.g. an
 * outlier window) and falling back to a fixed range when no finite values
 * are present. The pattern is duplicated across NowcastChart, TimeseriesChart,
 * and ExpectedReturnsTab — this module captures it once.
 */

export interface AutoRangeOptions {
  /** Fraction of the data range to pad on each side. Default 0.01 (1%). */
  paddingPct?: number;
  /**
   * Optional predicate to skip values at a given date/index.
   * Returning `true` excludes the value at that position.
   * If `dates` is omitted, the first argument is the empty string.
   */
  excludePredicate?: (date: string, index: number) => boolean;
  /** Returned when no finite values are found. Default `[0, 5]`. */
  fallback?: [number, number];
}

const DEFAULT_PADDING_PCT = 0.01;
const DEFAULT_FALLBACK: [number, number] = [0, 5];

function isFiniteNumber(v: number | null | undefined): v is number {
  return v !== null && v !== undefined && Number.isFinite(v);
}

/**
 * Compute a padded [min, max] y-axis range from a list of candidate values.
 *
 * Non-finite values (null/undefined/NaN/Infinity) are ignored. When `dates`
 * and `excludePredicate` are both supplied, indices for which the predicate
 * returns true are skipped.
 */
export function autoRangeYAxis(
  values: ReadonlyArray<number | null | undefined>,
  dates?: ReadonlyArray<string>,
  opts: AutoRangeOptions = {},
): [number, number] {
  return autoRangeYAxisMulti([values], dates, opts);
}

/**
 * Multi-series version: takes several parallel value arrays (e.g. p16, p84,
 * trend) and returns one padded [min, max] range across all of them. Each
 * inner array is indexed independently against `dates` for the exclusion
 * check.
 */
export function autoRangeYAxisMulti(
  series: ReadonlyArray<ReadonlyArray<number | null | undefined>>,
  dates?: ReadonlyArray<string>,
  opts: AutoRangeOptions = {},
): [number, number] {
  const paddingPct = opts.paddingPct ?? DEFAULT_PADDING_PCT;
  const fallback = opts.fallback ?? DEFAULT_FALLBACK;
  const exclude = opts.excludePredicate;

  let dataMin = Number.POSITIVE_INFINITY;
  let dataMax = Number.NEGATIVE_INFINITY;
  let found = false;

  for (const arr of series) {
    for (let i = 0; i < arr.length; i += 1) {
      if (exclude && exclude(dates?.[i] ?? '', i)) continue;
      const v = arr[i];
      if (!isFiniteNumber(v)) continue;
      if (v < dataMin) dataMin = v;
      if (v > dataMax) dataMax = v;
      found = true;
    }
  }

  if (!found) return [fallback[0], fallback[1]];

  const pad = (dataMax - dataMin) * paddingPct;
  return [dataMin - pad, dataMax + pad];
}
